import { getVariant, Box, InputProps } from '@apia/theme';
import { isChild, useCombinedRefs } from '@apia/util';
import React from 'react';
import { TIconName, TIconType } from '@apia/icons';
import { IconButton } from './buttons';
import ReactInputMask from 'react-input-mask';

export interface IField {
  className?: string;
  disabled?: boolean;
  isLoading?: boolean;
  readOnly?: boolean;
}

export interface IIconInput extends IField {
  additionalButtons?: React.ReactNode;
  additionalButtonsPosition?: 'before' | 'after';
  buttonProps?: Partial<React.ComponentProps<typeof IconButton>>;
  buttonRef?: React.RefObject<HTMLButtonElement>;
  hideButton?: boolean;
  icon?: TIconType | TIconName;
  inputProps?: Partial<InputProps>;
  inputRef?: React.RefObject<HTMLInputElement>;
  mask?: string;
  onClick?: (ev: React.MouseEvent) => void;
  onBlur?: React.FocusEventHandler;
  [key: string]: unknown;
}

export const IconInput = ({
  additionalButtons,
  additionalButtonsPosition = 'before',
  buttonProps,
  buttonRef,
  className,
  disabled,
  hideButton,
  icon = 'Search',
  inputProps,
  inputRef,
  isLoading,
  mask,
  onClick,
  onBlur,
  readOnly,
  ...rest
}: IIconInput) => {
  const { sx: inputSx, ...actualInputProps } = inputProps ?? {};
  const innerButtonRef = React.useRef<HTMLButtonElement>(null);
  const assignButtonRef = useCombinedRefs(buttonRef, innerButtonRef);

  const innerInputRef = React.useRef<HTMLInputElement>(null);
  const assignInputRef = useCombinedRefs(inputRef, innerInputRef);

  const parentRef = React.useRef<HTMLElement | null>(null);
  const handleBlur = React.useCallback(
    (ev: React.FocusEvent) => {
      if (
        onBlur &&
        (!ev.relatedTarget ||
          (ev.relatedTarget &&
            !isChild(
              ev.relatedTarget as HTMLElement,
              (current) => current === parentRef.current,
            )))
      )
        onBlur(ev);
    },
    [onBlur],
  );

  return (
    <Box
      {...getVariant('forms.iconInput')}
      className={`${className ?? ''} iconInput`}
      ref={parentRef}
      onBlur={handleBlur}
    >
      <ReactInputMask
        mask={mask ?? ''}
        onBlur={handleBlur}
        disabled={isLoading || disabled}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ref={assignInputRef as any}
        readOnly={readOnly}
        sx={{ variant: 'forms.input', ...inputSx }}
        data-variant="forms.input"
        {...rest}
        {...actualInputProps}
        maxLength={mask ? undefined : inputProps?.maxLength}
      />
      {additionalButtonsPosition === 'before' && additionalButtons}
      {!hideButton && (
        <IconButton
          onClick={onClick}
          icon={icon}
          disabled={readOnly ?? disabled}
          isLoading={isLoading}
          ref={assignButtonRef as React.Ref<HTMLButtonElement>}
          {...buttonProps}
        />
      )}
      {additionalButtonsPosition === 'after' && additionalButtons}
    </Box>
  );
};
